import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { UUID } from '@/types';
import { csrfToken } from '@/util/helpers/ajax-helpers';

export type TCompanyInfo = {
  company: string;
  street: string;
  city: string;
  postcode: string;
  country: string;
  taxNo: string;
};

export type TStripePlan = {
  id: string;
  rowLimit: number;
  isLite: boolean;
  title: string;
  price: number;
  startingPrice: number;
  currency: string;
  isTiredAmount: boolean;
  priceId: string;
};

export type TLineItem = {
  amount: number;
  description: string;
  unitPrice: number;
  quantity: number;
};

export type TSubscription = {
  stripeKey: string;
  address: TCompanyInfo;
  dbType: string;
  selectedPlan: string;
  nextPlanAt: string;
  nextPlan: string;
  stripePlans: TStripePlan[];
  cardShortName: string;
  currency: string;
  sendReceiptsTo: string;
  outstandingInvoice: unknown;
  subscriptionStatus: string;
  coupon: TStripeCoupon;
  lineItems: TLineItem[];
  subtotalItems: TLineItem[];
};

export type TStripeCoupon = {
  id: string;
  name: string;
  percentOff: number;
  amountOff: number;
  couponExpiry: Date;
};

export type TAvailableSubscription = {
  id: UUID;
  stripePlan: string;
  trevorUserEmail: string;
};

export type TDatasourceSubscription = {
  datasourceId: UUID;
  subscriptionId: UUID;
};

export type TCancelPlanRequest = {
  datasourceId: UUID;
  feedback?: string;
};

export const SUBSCRIPTION_TAG = 'StripeSubscription';

export const subscriptionApi = createApi({
  reducerPath: 'subscriptionApi',
  tagTypes: [SUBSCRIPTION_TAG],
  baseQuery: fetchBaseQuery({
    baseUrl: '/',
    credentials: 'same-origin',
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json;charset=UTF-8');
      headers.set('x-requested-with', 'XMLHttpRequest');

      const token = csrfToken();
      if (token) headers.set('x-csrf-token', token);

      return headers;
    },
  }),
  endpoints: (builder) => ({
    getSubscription: builder.query<TSubscription, UUID>({
      query: (datasourceId) => `datasources/${datasourceId}/api/subscription`,
      providesTags: [SUBSCRIPTION_TAG],
    }),
    activateSubscription: builder.mutation<
      { status: string },
      { plan: string; coupon: string; datasourceId: UUID }
    >({
      query({ plan, coupon, datasourceId }) {
        return {
          url: `datasources/${datasourceId}/api/subscription`,
          method: 'POST',
          body: { plan, coupon },
        };
      },
      invalidatesTags: [SUBSCRIPTION_TAG],
    }),
    getAvailableCreditSubscriptions: builder.query<
      TAvailableSubscription[],
      UUID
    >({
      query: () => `subscriptions/available-subscriptions`,
    }),
    addDatasourceToSubscription: builder.mutation<
      void,
      TDatasourceSubscription
    >({
      query(body: TDatasourceSubscription) {
        return {
          url: `datasources/${body.datasourceId}/set-subscription`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [SUBSCRIPTION_TAG],
    }),
    cancelSubscription: builder.mutation<void, TCancelPlanRequest>({
      query(body: TCancelPlanRequest) {
        return {
          url: `datasources/${body.datasourceId}/cancel-subscription`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [SUBSCRIPTION_TAG],
    }),
  }),
});

export const {
  useGetSubscriptionQuery,
  useActivateSubscriptionMutation,
  useGetAvailableCreditSubscriptionsQuery,
  useAddDatasourceToSubscriptionMutation,
  useCancelSubscriptionMutation,
} = subscriptionApi;
